import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Eye } from "lucide-react";
import { Card } from "@samklang/ui";
import { useKartlaggningStore, type Kartlaggning } from "@/stores/kartlaggning-store";
import { buildSamladBild, comparisonRows, metricPoints, neutrallyDescribeDifference, perspectiveLabel, sourceLine } from "./lib";

interface Props {
  readonly kartlaggningId: string;
}

export function KartlaggningCompilationScreen({ kartlaggningId }: Props) {
  const navigate = useNavigate();
  const kartlaggning = useKartlaggningStore((store) => store.getKartlaggning(kartlaggningId));
  const all = useKartlaggningStore((store) => store.listKartlaggningar)();

  const data = useMemo(() => (kartlaggning ? buildCompilation(kartlaggning, all) : undefined), [kartlaggning, all]);

  if (!kartlaggning || !data) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <Card>
          <h1 className="font-display text-2xl font-semibold">Kartläggningen finns inte.</h1>
          <button onClick={() => void navigate({ to: "/kartlaggningar" })} className="mt-4 font-semibold text-primary underline">Till listan</button>
        </Card>
      </section>
    );
  }

  return (
    <section className="mx-auto w-full max-w-4xl px-5 py-8 sm:px-8">
      <button onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId", params: { kartlaggningId } })} className="flex items-center gap-1 text-sm font-semibold text-primary">
        <ArrowLeft size={16} /> Till kartläggningen
      </button>
      <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight">Sammanställning</h1>
      <p className="mt-2 text-foreground-muted">
        Flera perspektiv på {kartlaggning.title} · {kartlaggning.participantReference || "lokal id saknas"}
      </p>

      {data.samladBild.agreementRows.length + data.samladBild.differenceRows.length === 0 ? (
        <Card className="mt-6">
          <p className="flex items-center gap-2 text-foreground-muted"><Eye size={18} /> Genomför minst två perspektiv för att se jämförelser.</p>
        </Card>
      ) : null}

      {data.samladBild.differenceRows.length > 0 ? (
        <Card className="mt-6">
          <h2 className="font-display text-xl font-semibold">Skillnader mellan perspektiv</h2>
          <p className="mt-1 text-sm text-foreground-muted">Neutralt beskrivna — inget perspektiv värderas som mer korrekt än ett annat.</p>
          <ul className="mt-4 space-y-3">
            {data.samladBild.differenceRows.map((row) => (
              <li key={row.questionId} className="rounded-2xl bg-surface-muted p-4 text-sm leading-relaxed">
                <strong>{row.question}</strong>
                <span className="mt-1 block">{neutrallyDescribeDifference(row)}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <CatCard title="Gemensamma nämnare">
          {data.samladBild.agreementRows.length === 0 ? <Placeholder /> : data.samladBild.agreementRows.map((row) => (
            <p key={row.questionId} className="text-sm leading-relaxed"><strong>{row.question}</strong> — {row.cells[0]?.answer}</p>
          ))}
        </CatCard>
        <CatCard title="Styrkor och fungerande situationer">
          {data.samladBild.strengths.length === 0 ? <Placeholder /> : data.samladBild.strengths.map((entry, index) => (
            <p key={`${entry.item.questionId}-${index}`} className="text-sm leading-relaxed">[{entry.label}] {entry.item.question}: {entry.item.answer}</p>
          ))}
        </CatCard>
        <CatCard title="Svårigheter">
          {data.samladBild.difficulties.length === 0 ? <Placeholder /> : data.samladBild.difficulties.map((entry, index) => (
            <p key={`${entry.item.questionId}-${index}`} className="text-sm leading-relaxed">[{entry.label}] {entry.item.question}: {entry.item.answer}</p>
          ))}
        </CatCard>
        <CatCard title="Behov">
          {data.samladBild.needs.length === 0 ? <Placeholder /> : data.samladBild.needs.map((entry, index) => (
            <p key={`${entry.item.questionId}-${index}`} className="text-sm leading-relaxed">[{entry.label}] {entry.item.question}: {entry.item.answer}</p>
          ))}
        </CatCard>
      </div>

      <h2 className="mt-9 font-display text-2xl font-semibold">Sida vid sida per fråga</h2>
      <p className="mt-1 text-sm text-foreground-muted">Elevens perspektiv visas alltid tydligt först.</p>
      <div className="mt-4 grid gap-4">
        {data.rows.map((row) => (
          <Card key={row.questionId} padding="md">
            <h3 className="font-display text-lg font-semibold">{row.question}</h3>
            <span className="mt-1 inline-block rounded-full bg-surface-muted px-3 py-1 text-xs font-medium text-foreground-muted">{row.theme}</span>
            <div className="mt-4 divide-y divide-border/50">
              {row.cells.map((cell) => (
                <div key={cell.perspective} className={`py-3 ${cell.perspective === "elev" ? "rounded-2xl bg-primary-soft/40 px-3" : ""}`}>
                  <p className="text-sm font-semibold text-primary">{cell.label}</p>
                  <p className="mt-1">{cell.answer}</p>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-foreground-subtle">{row.source}</p>
          </Card>
        ))}
      </div>

      {data.development.length > 0 ? (
        <>
          <h2 className="mt-9 font-display text-2xl font-semibold">Utveckling över tid</h2>
          <p className="mt-1 text-sm text-foreground-muted">Skattningar per perspektiv över tidigare kartläggningar för samma elev.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {data.development.map((metric) => (
              <Card key={metric.key} className="px-5 py-4">
                <p className="text-sm font-semibold text-foreground-muted">{metric.label}</p>
                <p className="mt-1 font-display text-lg">{metric.sequence}</p>
              </Card>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}

function buildCompilation(kartlaggning: Kartlaggning, all: readonly Kartlaggning[]) {
  const rows = comparisonRows(kartlaggning).map((row) => ({
    ...row,
    source: row.questionId ? sourceOfQuestion(kartlaggning, row.questionId) : "",
  }));
  const samladBild = buildSamladBild(kartlaggning);

  const points = metricPoints(kartlaggning.participantReference, all);
  const byKey = new Map<string, { key: string; label: string; sequence: string }>();
  for (const point of points) {
    const existing = byKey.get(point.metricKey);
    if (existing) {
      byKey.set(point.metricKey, { ...existing, sequence: `${existing.sequence} · ${perspectiveLabel(point.perspective, point.role)} ${point.value}` });
    } else {
      byKey.set(point.metricKey, { key: point.metricKey, label: point.metricLabel, sequence: `${perspectiveLabel(point.perspective, point.role)} ${point.value}` });
    }
  }
  const development = [...byKey.values()];
  return { rows, samladBild, development };
}

function sourceOfQuestion(kartlaggning: Kartlaggning, questionId: string): string {
  for (const run of kartlaggning.runs) {
    if (run.status !== "done" || !run.summary) continue;
    const item = run.summary.find((candidate) => candidate.questionId === questionId);
    if (item) return sourceLine(run, run.perspective);
  }
  return "";
}

function CatCard({ title, children }: { readonly title: string; readonly children: React.ReactNode }) {
  return (
    <Card className="px-5 py-4">
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      <div className="mt-3 space-y-3">{children}</div>
    </Card>
  );
}

function Placeholder() {
  return <p className="text-sm text-foreground-subtle">Inga uppgifter ännu.</p>;
}
